import { useEffect, useState, useCallback } from "react";
import { api, errMsg, today } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import AddRepairDialog from "@/components/AddRepairDialog";
import AddQuickDialog from "@/components/AddQuickDialog";
import { useNavigate } from "react-router-dom";
import {
  Wrench, Zap, CalendarDays, History, Users, Settings, AlertCircle,
  Trash2, CheckCircle2, ChevronRight,
} from "lucide-react";
import { formatHours } from "@/lib/api";
import { toast } from "sonner";

function ActionCard({ testid, onClick, title, subtitle, icon: Icon, colorClasses, badge }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`group relative overflow-hidden text-left rounded-xl border-2 p-5 md:p-6 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg active:translate-y-0 focus:outline-none focus:ring-4 focus:ring-blue-100 ${colorClasses}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-11 h-11 rounded-lg bg-white/25 backdrop-blur-sm flex items-center justify-center shrink-0">
            <Icon className="w-6 h-6" />
          </div>
          <div className="min-w-0">
            <div className="font-display font-bold text-lg md:text-xl leading-tight">{title}</div>
            {subtitle && <div className="text-sm opacity-90 mt-0.5">{subtitle}</div>}
          </div>
        </div>
        <ChevronRight className="w-5 h-5 opacity-70 group-hover:translate-x-0.5 transition-transform shrink-0" />
      </div>
      {badge && (
        <span className="absolute top-3 right-3 text-xs font-bold bg-white/90 text-slate-900 px-2 py-1 rounded-full">
          {badge}
        </span>
      )}
    </button>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [workshop, setWorkshop] = useState(null);
  const [stats, setStats] = useState({ mechanics: 0, active: 0, quickToday: 0 });
  const [settings, setSettings] = useState({ ratio: 0.6, quick_ratio: 0.6 });
  const [overdue, setOverdue] = useState([]);
  const [newOpen, setNewOpen] = useState(false);
  const [newQuickOpen, setNewQuickOpen] = useState(false);

  const load = useCallback(async () => {
    const todayIso = today();
    const [meRes, mechRes, repRes, quickRes, settingsRes] = await Promise.all([
      api.get("/workshop/me"),
      api.get("/mechanics"),
      api.get("/repairs"),
      api.get(`/quick-repairs?date_filter=${todayIso}`),
      api.get("/settings"),
    ]);
    setWorkshop(meRes.data);
    setSettings(settingsRes.data);
    const notCompleted = repRes.data.filter((r) => r.status !== "completed");
    const active = notCompleted.filter((r) => (r.segments || []).some((s) => s.date >= todayIso));
    const overdueList = notCompleted.filter((r) =>
      (r.segments || []).length > 0 && (r.segments || []).every((s) => s.date < todayIso)
    );
    setStats({
      mechanics: mechRes.data.length,
      active: active.length,
      quickToday: quickRes.data.length,
    });
    setOverdue(overdueList);
  }, []);

  useEffect(() => { load(); }, [load]);

  const completeOverdue = async (id) => {
    try {
      await api.put(`/repairs/${id}/complete`);
      toast.success("Marcada como completada");
      load();
    } catch (e) {
      toast.error(errMsg(e, "No se pudo completar"));
    }
  };
  const deleteOverdue = async (id) => {
    if (!window.confirm("¿Eliminar esta cita atrasada?")) return;
    try {
      await api.delete(`/repairs/${id}`);
      toast.success("Cita eliminada");
      load();
    } catch (e) {
      toast.error(errMsg(e, "No se pudo eliminar"));
    }
  };

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <header>
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900">
          {workshop?.name || "Taller"}
        </h1>
        <p className="text-slate-500 mt-1">
          {stats.mechanics} {stats.mechanics === 1 ? "mecánico" : "mecánicos"} · {stats.active} {stats.active === 1 ? "cita activa" : "citas activas"} · {stats.quickToday} {stats.quickToday === 1 ? "rápida hoy" : "rápidas hoy"}
        </p>
      </header>

      {/* Overdue warning */}
      {overdue.length > 0 && (
        <Card className="border-amber-300 bg-amber-50" data-testid="overdue-card">
          <CardContent className="pt-5">
            <div className="flex items-start gap-3 mb-3">
              <AlertCircle className="w-6 h-6 text-amber-600 shrink-0" />
              <div>
                <h2 className="font-display font-bold text-lg text-amber-900">
                  {overdue.length} {overdue.length === 1 ? "cita atrasada" : "citas atrasadas"}
                </h2>
                <p className="text-sm text-amber-800">Ciérralas o elimínalas para limpiar la vista.</p>
              </div>
            </div>
            <ul className="divide-y divide-amber-200">
              {overdue.map((r) => (
                <li key={r.id} className="py-2 flex items-start justify-between gap-2 flex-wrap" data-testid={`overdue-item-${r.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {r.license_plate && <span className="font-mono font-bold text-sm bg-white px-2 py-0.5 rounded">{r.license_plate}</span>}
                      {(r.brand || r.model) && <span className="text-sm text-slate-700">{r.brand} {r.model}</span>}
                    </div>
                    <div className="text-sm text-slate-900 mt-0.5">{r.description}</div>
                    <div className="text-xs text-amber-700 mt-0.5">{formatHours(r.estimated_hours)}</div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button variant="ghost" size="icon" className="h-9 w-9 text-green-600" onClick={() => completeOverdue(r.id)} data-testid={`complete-overdue-${r.id}`}>
                      <CheckCircle2 className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-9 w-9 text-red-600" onClick={() => deleteOverdue(r.id)} data-testid={`delete-overdue-${r.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Row 1: Nueva Cita */}
      <section>
        <h2 className="font-display text-sm uppercase tracking-wider text-slate-500 mb-3">Nueva cita</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ActionCard
            testid="home-new-general"
            onClick={() => setNewOpen(true)}
            title="Nueva Cita"
            subtitle={`Mecánica general · regla ${Math.round(settings.ratio * 100)}/${100 - Math.round(settings.ratio * 100)}`}
            icon={Wrench}
            colorClasses="bg-blue-700 hover:bg-blue-800 border-blue-700 text-white"
          />
          <ActionCard
            testid="home-new-quick"
            onClick={() => setNewQuickOpen(true)}
            title="Nueva Rápida"
            subtitle={`Mecánica rápida · regla ${Math.round((settings.quick_ratio ?? 0.6) * 100)}/${100 - Math.round((settings.quick_ratio ?? 0.6) * 100)}`}
            icon={Zap}
            colorClasses="bg-amber-500 hover:bg-amber-600 border-amber-500 text-white"
          />
        </div>
      </section>

      {/* Row 2: Agendas */}
      <section>
        <h2 className="font-display text-sm uppercase tracking-wider text-slate-500 mb-3">Agendas</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ActionCard
            testid="home-agenda-general"
            onClick={() => navigate("/agenda")}
            title="Agenda Taller"
            subtitle="Ver citas de mecánica general"
            icon={CalendarDays}
            colorClasses="bg-slate-900 hover:bg-slate-800 border-slate-900 text-white"
            badge={stats.active > 0 ? stats.active : null}
          />
          <ActionCard
            testid="home-agenda-quick"
            onClick={() => navigate("/rapida")}
            title="Agenda Rápida"
            subtitle="Ver tareas rápidas"
            icon={Zap}
            colorClasses="bg-slate-800 hover:bg-slate-700 border-slate-800 text-white"
            badge={stats.quickToday > 0 ? stats.quickToday : null}
          />
        </div>
      </section>

      {/* Row 3: Gestión */}
      <section>
        <h2 className="font-display text-sm uppercase tracking-wider text-slate-500 mb-3">Gestión</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <ActionCard
            testid="home-vehiculos"
            onClick={() => navigate("/vehiculos")}
            title="Historial vehículos"
            subtitle="Buscar por matrícula"
            icon={History}
            colorClasses="bg-white hover:bg-slate-50 border-slate-200 text-slate-900"
          />
          <ActionCard
            testid="home-mecanicos"
            onClick={() => navigate("/mecanicos")}
            title="Mecánicos"
            subtitle={`${stats.mechanics} en el equipo`}
            icon={Users}
            colorClasses="bg-white hover:bg-slate-50 border-slate-200 text-slate-900"
          />
          <ActionCard
            testid="home-ajustes"
            onClick={() => navigate("/ajustes")}
            title="Ajustes"
            subtitle="Ratio de capacidad, festivos"
            icon={Settings}
            colorClasses="bg-white hover:bg-slate-50 border-slate-200 text-slate-900"
          />
        </div>
      </section>

      <AddRepairDialog
        open={newOpen}
        onOpenChange={setNewOpen}
        onCreated={() => { setNewOpen(false); load(); }}
      />

      <AddQuickDialog
        open={newQuickOpen}
        onOpenChange={setNewQuickOpen}
        onCreated={() => { setNewQuickOpen(false); load(); }}
      />
    </div>
  );
}
